import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ANALYTICS } from './analyticsTokens';
import {
  formatRunAt,
  problemLines,
  readinessCsv,
  VERDICT_LABELS,
  VERDICT_MEANINGS,
  verdictNote,
  type AuditDistrict,
  type AuditTeacherResult,
  type AuditVerdict,
} from './progressReportAuditApi';

const base = { fontFamily: ANALYTICS.font } as const;
const cardSx = { bgcolor: ANALYTICS.surface, borderRadius: '8px', border: `1px solid ${ANALYTICS.grey50}`, p: '20px 24px' } as const;
const lineSx = { ...base, fontSize: 14, lineHeight: '20px', color: ANALYTICS.muted } as const;
const cellSx = { ...base, fontSize: 14, lineHeight: '20px', p: '8px 10px', textAlign: 'left', verticalAlign: 'top', color: ANALYTICS.ink, borderBottom: `1px solid ${ANALYTICS.divider}` } as const;
const numSx = { ...cellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums' } as const;
const headSx = { ...cellSx, fontWeight: 700, color: ANALYTICS.muted } as const;
const buttonSx = { ...base, fontSize: 14, lineHeight: '20px', color: ANALYTICS.primary, background: 'none', border: 0, p: 0, cursor: 'pointer', textDecoration: 'underline' } as const;

const VERDICT_COLORS: Record<AuditVerdict, { color: string; bgcolor: string }> = {
  ready: { color: ANALYTICS.green700, bgcolor: ANALYTICS.green50 },
  check: { color: ANALYTICS.yellow900, bgcolor: ANALYTICS.yellow100 },
  empty: { color: ANALYTICS.red600, bgcolor: ANALYTICS.red100 },
  unanswered: { color: ANALYTICS.muted, bgcolor: ANALYTICS.grey25 },
};

const VERDICTS: AuditVerdict[] = ['ready', 'check', 'empty', 'unanswered'];

const ROWS_SHOWN = 25;

function Legend() {
  return (
    <Box data-testid="readiness-legend" sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
      {VERDICTS.map((verdict) => (
        <Typography key={verdict} sx={lineSx}>
          <Box component="strong" sx={{ color: VERDICT_COLORS[verdict].color }}>
            {VERDICT_LABELS[verdict]}
          </Box>
          {` ${VERDICT_MEANINGS[verdict]}`}
        </Typography>
      ))}
    </Box>
  );
}

function Pill({ verdict, count }: { verdict: AuditVerdict; count: number }) {
  return (
    <Box
      component="span"
      data-testid="readiness-pill"
      data-verdict={verdict}
      sx={{ ...base, ...VERDICT_COLORS[verdict], fontSize: 13, lineHeight: '20px', fontWeight: 700, borderRadius: '8px', p: '6px 14px' }}
    >
      {`${count} ${VERDICT_LABELS[verdict]}`}
    </Box>
  );
}

function Verdict({ verdict }: { verdict: AuditVerdict }) {
  return (
    <Box
      component="span"
      data-testid="readiness-verdict"
      sx={{ ...base, ...VERDICT_COLORS[verdict], display: 'inline-block', fontSize: 12, lineHeight: '16px', fontWeight: 700, borderRadius: '6px', p: '2px 8px' }}
    >
      {VERDICT_LABELS[verdict]}
    </Box>
  );
}

function download(name: string, csv: string) {
  if (typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

function csvName(districtName: string): string {
  return `teacher-readiness-${districtName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}.csv`;
}

function TeacherRow({ row, window }: { row: AuditTeacherResult; window: string | undefined }) {
  const [open, setOpen] = useState(false);
  const problems = row.problemStudents;
  const ownWindow = row.currentWindow && row.currentWindow !== window ? ` (${row.currentWindow})` : '';
  return (
    <>
      <Box component="tr" data-testid="readiness-row" data-verdict={row.verdict}>
        <Box component="td" data-testid="readiness-name" sx={cellSx}>
          {row.name}
        </Box>
        <Box component="td" data-testid="readiness-schools" sx={cellSx}>
          {row.schools.join(', ')}
        </Box>
        <Box component="td" data-testid="readiness-students" sx={numSx}>
          {row.students}
        </Box>
        <Box component="td" data-testid="readiness-current" sx={numSx}>
          {`${row.currentBenchmark}${ownWindow}`}
        </Box>
        <Box component="td" data-testid="readiness-flashlight" sx={numSx}>
          {row.flashlightStudents === undefined ? '—' : row.flashlightStudents}
        </Box>
        <Box component="td" data-testid="readiness-problems" sx={numSx}>
          {problems > 0 && row.problems.length > 0 ? (
            <Box component="button" type="button" data-testid="readiness-problems-toggle" aria-expanded={open} onClick={() => setOpen(!open)} sx={buttonSx}>
              {problems}
            </Box>
          ) : (
            problems
          )}
        </Box>
        <Box component="td" sx={cellSx}>
          <Verdict verdict={row.verdict} />
          <Box component="span" data-testid="readiness-note" sx={{ ...lineSx, ml: '8px' }}>
            {verdictNote(row, window)}
          </Box>
        </Box>
      </Box>
      {open && (
        <Box component="tr" data-testid="readiness-problem-list">
          <Box component="td" colSpan={7} sx={{ ...cellSx, bgcolor: ANALYTICS.grey25, color: ANALYTICS.muted, pl: '40px' }}>
            {problemLines(row).map((line) => (
              <Typography key={line} data-testid="readiness-problem" sx={lineSx}>
                {line}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </>
  );
}

function DistrictReadiness({ row }: { row: AuditDistrict }) {
  const [all, setAll] = useState(false);
  const r = row.readiness;
  if (!r) return null;
  const teachers = all ? r.teachers : r.teachers.slice(0, ROWS_SHOWN);
  const openable = row.variance?.onFlashlightRoster;
  const checked = r.ready + r.check + r.empty;
  const meta = [
    `${checked} ${checked === 1 ? 'teacher' : 'teachers'} checked`,
    ...(r.unanswered > 0 ? [`${r.unanswered} unanswered`] : []),
    ...(openable === undefined ? [] : [`${openable.toLocaleString('en-US')} students openable district-wide`]),
    ...(r.currentWindow ? [`current window ${r.currentWindow}`] : []),
  ].join(' · ');
  const audited = [
    ...(row.auditedAt ? [`Audited ${formatRunAt(row.auditedAt)}`] : []),
    ...(row.skippedAt ? [`a later run on ${formatRunAt(row.skippedAt)} did not reach this district, so these rows are older than the run above`] : []),
    ...(row.error ? [row.error] : []),
  ].join(' · ');
  const head = ['Teacher', 'Schools', 'Students', `${r.currentWindow ?? 'Current'} benchmark`, 'FL360 students in classes', 'Problem students', 'Verdict'];
  return (
    <Box data-testid="readiness-card" data-district={row.districtId} sx={cardSx}>
      <Typography component="h2" sx={{ ...base, fontSize: 18, lineHeight: '24px', fontWeight: 700, color: ANALYTICS.ink, m: 0 }}>
        {row.districtName || row.districtId}
      </Typography>
      <Typography data-testid="readiness-meta" sx={{ ...base, fontSize: 13, lineHeight: '20px', color: ANALYTICS.muted }}>
        {meta}
      </Typography>
      {audited && (
        <Typography data-testid="readiness-audited" sx={{ ...base, fontSize: 13, lineHeight: '20px', color: row.skippedAt || row.error ? ANALYTICS.red600 : ANALYTICS.muted }}>
          {audited}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: '10px', m: '14px 0 16px', flexWrap: 'wrap' }}>
        {VERDICTS.map((verdict) => (
          <Pill key={verdict} verdict={verdict} count={r[verdict]} />
        ))}
      </Box>
      <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%' }}>
        <Box component="thead">
          <Box component="tr">
            {head.map((label, i) => (
              <Box key={label} component="th" data-testid="readiness-head" sx={i >= 2 && i <= 5 ? { ...headSx, textAlign: 'right' } : headSx}>
                {label}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {teachers.map((teacher) => (
            <TeacherRow key={teacher.sub} row={teacher} window={r.currentWindow} />
          ))}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: '24px', mt: '12px', alignItems: 'baseline' }}>
        {!all && r.teachers.length > ROWS_SHOWN && (
          <Box component="button" type="button" data-testid="readiness-show-all" onClick={() => setAll(true)} sx={buttonSx}>
            {`Show all ${r.teachers.length} teachers`}
          </Box>
        )}
        <Box component="button" type="button" data-testid="readiness-csv" onClick={() => download(csvName(row.districtName || row.districtId), readinessCsv(row))} sx={buttonSx}>
          Download CSV
        </Box>
      </Box>
      <Typography sx={{ ...base, mt: '10px', fontSize: 12, lineHeight: '16px', color: ANALYTICS.disabled }}>
        Students is what the Progress Reports table would list for this teacher. Problem students are reports that would be missing a piece or rest on an uncertain identity; every other value on a report is computed from the same rows the checker read.
      </Typography>
    </Box>
  );
}

export function TeacherReadiness({ districts }: { districts: AuditDistrict[] }) {
  const graded = districts.filter((row) => row.readiness);
  const without = districts.filter((row) => !row.readiness).map((row) => row.districtName || row.districtId);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      <Legend />
      {graded.map((row) => (
        <DistrictReadiness key={row.districtId} row={row} />
      ))}
      {without.length > 0 && (
        <Typography data-testid="readiness-without" sx={{ ...lineSx, p: '14px 20px', border: `1px dashed ${ANALYTICS.grey50}`, borderRadius: '8px' }}>
          {`${without.join(' · ')}: no teachers graded this run.`}
        </Typography>
      )}
    </Box>
  );
}
